import { useQuery } from 'react-query';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import pathfinder from '@fiverr-private/pathfinder';
import { SectionTypes } from '@fiverr-private/ai_brief';
import { logger } from '../../utils/logger';
import { addCustomSections } from '../../components/Sections/useCustomSections';
import { seedSectionsFromApi } from '../useEnrichedBuyerBrief/utils/seedSectionsFromApi';
import { transformApiAttachments } from '../useEnrichedBuyerBrief/utils/transformAttachments';
import { BuyerBriefOverviewResponse, SectionsApiResponse } from '../useEnrichedBuyerBrief/types';
import { buildBriefClassification } from './utils/buildBriefClassification';

const DUPLICATE_BRIEF_PREFIX = '[Copy]';

const prepareDuplicateBriefData = (brief: BuyerBriefOverviewResponse, seededSections: any) => ({
  ...cloneDeep(brief),
  title: `${DUPLICATE_BRIEF_PREFIX} ${brief.title}`,
  budgetInCents: null,
  budgetRangeInCents: null,
  pricingType: null,
  deliveryDateEpochMs: null,
  sections: seededSections,
});

export interface UseEnrichedDuplicateBriefParams {
  briefId: string;
  enabled?: boolean;
  onError?: (error: Error) => void;
}

export const useEnrichedDuplicateBrief = ({ briefId, enabled = true, onError }: UseEnrichedDuplicateBriefParams) =>
  useQuery(
    ['EnrichedDuplicateBrief', briefId],
    async () => {
      const { data: brief } = await axios.get<BuyerBriefOverviewResponse>(
        pathfinder('get_dm_brief_buyer_overview', { briefId })
      );

      if (!brief) {
        logger.error('Duplicate brief data not found', {
          briefId,
          response: brief,
        });
        throw new Error('Duplicate brief data not found');
      }

      const { data: sectionApi } = await axios.post<SectionsApiResponse>(pathfinder('get_brief_sections'), {
        category_id: brief.categoryId,
        sub_category_id: brief.subCategoryId ?? 0,
        nested_sub_category_id: brief.nestedSubCategoryId ?? brief.subCategoryId ?? 0,
      });

      if (!sectionApi?.sections) {
        logger.error('Sections API data not found for duplicate brief', {
          briefId,
          sectionApi,
        });
        throw new Error('Sections API data not found');
      }

      const seededSections = seedSectionsFromApi(brief, sectionApi);

      const briefCopy = prepareDuplicateBriefData(brief, seededSections);

      const classification = buildBriefClassification({ brief, sectionApi });

      // Ensure addCustomSections returns the brief (it mutates briefCopy)
      const enrichedBrief =
        addCustomSections({
          brief: briefCopy,
          additionalData: classification,
          enableEnhancedUx: true,
          rollouts: { inMustangsPersonBrief: true },
        }) || briefCopy;

      const transformedAttachments = brief.attachments?.length ? transformApiAttachments(brief.attachments) : undefined;

      const enrichedSections = enrichedBrief?.sections?.map((s) => {
        if (s.id === 'budget_and_timeline') {
          return { ...s, content: null, included: true };
        }
        if (s.type === SectionTypes.attachments && transformedAttachments) {
          return { ...s, attachments: transformedAttachments, included: true };
        }
        return s;
      });

      if (enrichedSections) {
        enrichedBrief.sections = enrichedSections;
      }

      return {
        brief: enrichedBrief,
        extraWaveStatus: brief.extraWaveStatus,
        briefClassification: classification,
      };
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error: Error) => {
        logger.error('useEnrichedDuplicateBrief failed', {
          briefId,
          error,
        });

        onError?.(error);
      },
    }
  );
